import { useState, useEffect, useCallback } from 'react';
import { runGetCurrency, getCurrentCurrency } from '../actions/runGetCurrency';
import { runSetCurrency, getPreferredCurrency } from '../actions/runSetCurrency';

/**
 * Custom hook for managing currency state and operations
 */
export const useCurrency = (options = {}) => {
    const {
        autoInit = true,
        defaultCountry = 'AU',
        defaultCurrency = 'AUD',
        defaultSymbol = '$'
    } = options;

    // State
    const [currencies, setCurrencies] = useState([]);
    const [currentCurrency, setCurrentCurrency] = useState(null);
    const [isLoading, setIsLoading] = useState(autoInit);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Get current country
    const getCurrentCountry = useCallback(() => {
        return getCurrentCurrency() || getPreferredCurrency() || defaultCountry;
    }, [defaultCountry]);

    // Fetch currencies
    const fetchCurrencies = useCallback(async (country = null) => {
        try {
            setIsLoading(true);
            setError(null);
            
            const targetCountry = country || getCurrentCountry();
            const result = await runGetCurrency(targetCountry);
            
            if (result.success) {
                setCurrencies(result.currencies || []);
                setCurrentCurrency(result.currentCountry || null);
                return result;
            } else {
                throw new Error(result.error || 'Failed to fetch currencies');
            }
        } catch (err) {
            setError(err.message);
            console.error('Currency fetch error:', err);
            return { success: false, error: err.message };
        } finally {
            setIsLoading(false);
        }
    }, [getCurrentCountry]);

    // Set currency
    const setCurrency = useCallback(async (countryCode, setCurrencyOptions = {}) => {
        try {
            setError(null);
            
            const result = await runSetCurrency(countryCode, {
                autoRedirect: true,
                rememberSelection: true,
                returnTo: window.location.pathname || '/',
                ...setCurrencyOptions
            });
            
            if (result.success) {
                // Update current currency in state if not redirecting
                if (!result.autoRedirect) {
                    const newCurrency = currencies.find(curr => curr.country === countryCode);
                    if (newCurrency) {
                        setCurrentCurrency({
                            currency: {
                                isoCode: newCurrency.code,
                                name: newCurrency.fullName,
                                symbol: newCurrency.symbol
                            },
                            isoCode: countryCode,
                            name: newCurrency.countryName
                        });
                    }
                }
            }
            
            return result;
        } catch (err) {
            setError(err.message);
            console.error('Currency set error:', err);
            return { success: false, error: err.message };
        }
    }, [currencies]);

    // Get currency by country code
    const getCurrencyByCountry = useCallback((countryCode) => {
        return currencies.find(currency => currency.country === countryCode);
    }, [currencies]);

    // Get formatted currency display
    const formatCurrencyDisplay = useCallback((currency, options = {}) => {
        const {
            showSymbol = true,
            showCode = true,
            showName = false,
            separator = ' '
        } = options;

        if (!currency) return '';

        const parts = [];
        
        if (showSymbol && currency.symbol) {
            parts.push(currency.symbol);
        }
        
        if (showCode && currency.code) {
            parts.push(currency.code);
        }
        
        if (showName && currency.fullName) {
            parts.push(currency.fullName);
        }

        return parts.join(separator);
    }, []);

    // Initialize on mount
    useEffect(() => {
        if (autoInit && !isInitialized) {
            fetchCurrencies().finally(() => {
                setIsInitialized(true);
            });
        }
    }, [autoInit, isInitialized, fetchCurrencies]);

    // Event listeners
    useEffect(() => {
        const handleCurrencyFetched = (event) => {
            const { currencies: fetchedCurrencies, currentCountry, success } = event.detail;
            if (success) {
                setCurrencies(fetchedCurrencies || []);
                setCurrentCurrency(currentCountry || null);
                setIsLoading(false);
                setError(null);
            }
        };

        const handleCurrencyChanged = (event) => {
            const { countryCode, success } = event.detail;
            if (success && currencies.length > 0) {
                const newCurrency = getCurrencyByCountry(countryCode);
                if (newCurrency) {
                    setCurrentCurrency({
                        currency: {
                            isoCode: newCurrency.code,
                            name: newCurrency.fullName,
                            symbol: newCurrency.symbol
                        },
                        isoCode: countryCode,
                        name: newCurrency.countryName
                    });
                }
            }
        };

        const handleCurrencyError = (event) => {
            const { error: errorMessage } = event.detail;
            setError(errorMessage);
            setIsLoading(false);
        };

        const handleCurrencyInitialized = (event) => {
            const { currency, currencies: initCurrencies, country } = event.detail;
            if (initCurrencies) {
                setCurrencies(initCurrencies);
            }
            if (currency) {
                setCurrentCurrency({
                    currency,
                    isoCode: country,
                    name: country
                });
            }
            setIsInitialized(true);
        };

        // Add event listeners
        window.addEventListener('currency:fetched', handleCurrencyFetched);
        window.addEventListener('currency:changed', handleCurrencyChanged);
        window.addEventListener('currency:error', handleCurrencyError);
        window.addEventListener('currency_initialised', handleCurrencyInitialized);

        // Cleanup
        return () => {
            window.removeEventListener('currency:fetched', handleCurrencyFetched);
            window.removeEventListener('currency:changed', handleCurrencyChanged);
            window.removeEventListener('currency:error', handleCurrencyError);
            window.removeEventListener('currency_initialised', handleCurrencyInitialized);
        };
    }, [currencies, getCurrencyByCountry]);

    return {
        // State
        currencies,
        currentCurrency,
        isLoading,
        error,
        isInitialized,
        
        // Actions
        fetchCurrencies,
        setCurrency,
        
        // Utilities
        getCurrentCountry,
        getCurrencyByCountry,
        formatCurrencyDisplay,
        
        // Computed values
        currentCountryCode: currentCurrency?.isoCode || getCurrentCountry(),
        currentCurrencyCode: currentCurrency?.currency?.isoCode || defaultCurrency,
        currentCurrencySymbol: currentCurrency?.currency?.symbol || defaultSymbol
    };
};

export default useCurrency;
